import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from '../../shared/services/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BACKEND_API_BASE_URL } from '../../shared/config/app-settings.tokens';

/**
 * Dashboard counter response interface
 */
interface DashboardCounters {
  inboxCounts: { total: number; today: number };
  myRequestsCounts: { total: number; today: number };
  executedCounts: { total: number; today: number };
  completedCounts: { total: number; today: number };
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  // Loading state for counters
  public isLoadingCounters = true;

  // Displayed (animated) values
  public inboxCount = 0;
  public requestsIssued = 0;
  public sentRequests = 0;
  public closedRequests = 0;

  // Flags toggled when each counter finishes — used to trigger the CSS pop
  public inboxDone = false;
  public requestsIssuedDone = false;
  public sentRequestsDone = false;
  public closedRequestsDone = false;

  private destroy$ = new Subject<void>();
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private readonly backendApiBaseUrl = inject(BACKEND_API_BASE_URL);

  ngOnInit(): void {
    this.loadDashboardCounters();
  }

  /**
   * Load dashboard counter data from API
   */
  private loadDashboardCounters(): void {
    const apiUrl = `${this.backendApiBaseUrl}/request/user/dashboard`;

    this.http.get<DashboardCounters>(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: DashboardCounters) => {
          this.isLoadingCounters = false;
          this.animateCount('inboxCount',     'inboxDone',            response.inboxCounts.total);
          this.animateCount('requestsIssued', 'requestsIssuedDone',   response.myRequestsCounts.total);
          this.animateCount('sentRequests',   'sentRequestsDone',     response.executedCounts.total);
          this.animateCount('closedRequests', 'closedRequestsDone',   response.completedCounts.total);
        },
        error: (error: any) => {
          console.error('[Dashboard] Error loading counters:', error);
          this.isLoadingCounters = false;
        }
      });
  }

  /**
   * Animate a numeric property from 0 to `target` over `duration` ms.
   * Sets the matching `doneFlag` to true when complete to trigger the CSS pop.
   * Uses an ease-out cubic curve for a natural deceleration feel.
   */
  private animateCount(
    prop: 'inboxCount' | 'requestsIssued' | 'sentRequests' | 'closedRequests',
    doneFlag: 'inboxDone' | 'requestsIssuedDone' | 'sentRequestsDone' | 'closedRequestsDone',
    target: number,
    duration = 1200
  ): void {
    const start = performance.now();
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      (this as any)[prop] = Math.round(easeOut(progress) * target);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        (this as any)[prop] = target;
        (this as any)[doneFlag] = true;
      }
    };

    requestAnimationFrame(tick);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
